(function () {
  'use strict';

  class InfiniteScroll {
    constructor(sentinel) {
      this.sentinel = sentinel;
      this.nextUrl  = sentinel.dataset.nextUrl;
      this.loading  = false;
      this.done     = !this.nextUrl;

      sentinel._infiniteScrollInstance = this;

      if (this.done) return;

      this.observer = new IntersectionObserver(
        this._onIntersect.bind(this),
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this.sentinel);
    }

    _onIntersect(entries) {
      if (!entries[0].isIntersecting) return;
      if (this.loading || this.done) return;
      this._loadNext();
    }

    async _loadNext() {
      this.loading = true;
      this._showSpinner();

      try {
        const response = await fetch(this.nextUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const doc  = new DOMParser().parseFromString(html, 'text/html');

        const newList     = doc.querySelector('product-list');
        const newSentinel = doc.querySelector('.infinite-scroll-sentinel');

        if (newList) {
          const currentList =
            document.querySelector(`product-list#${this.sentinel.dataset.listId}`) ||
            document.querySelector('product-list');

          if (currentList) {
            Array.from(newList.children).forEach(item => {
              item.removeAttribute('reveal-on-scroll');
              item.querySelectorAll('[reveal-on-scroll]').forEach(el => {
                el.removeAttribute('reveal-on-scroll');
              });

              item.style.opacity    = '0';
              item.style.transform  = 'translateY(16px)';
              item.style.transition = 'opacity 0.25s ease, transform 0.25s ease';

              currentList.appendChild(item);

              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  item.style.opacity   = '1';
                  item.style.transform = 'translateY(0)';
                });
              });
            });
          }
        }

        if (newSentinel && newSentinel.dataset.nextUrl) {
          this.nextUrl = newSentinel.dataset.nextUrl;
          this.done    = false;
        } else {
          this.done = true;
          this.observer.unobserve(this.sentinel);
          this.sentinel.remove();
        }

      } catch (err) {
        console.error('[InfiniteScroll] Failed to load next page:', err);
        this._showFallbackButton();
      } finally {
        this.loading = false;
        this._hideSpinner();
      }
    }

    _showSpinner() {
      let spinner = this.sentinel.querySelector('.infinite-scroll-spinner');
      if (!spinner) {
        spinner = document.createElement('div');
        spinner.className = 'infinite-scroll-spinner';
        spinner.innerHTML = `
          <style>
            .is-wrap {
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 2.5rem 0;
            }
            .is-loader {
              width: 65px;
              aspect-ratio: 1;
              position: relative;
            }
            .is-loader:before,
            .is-loader:after {
              content: "";
              position: absolute;
              border-radius: 50px;
              box-shadow: 0 0 0 3px inset currentColor;
              animation: is-l4 2.5s infinite;
            }
            .is-loader:after {
              animation-delay: -1.25s;
            }
            @keyframes is-l4 {
              0%    { inset: 0 35px 35px 0; }
              12.5% { inset: 0 35px 0 0; }
              25%   { inset: 35px 35px 0 0; }
              37.5% { inset: 35px 0 0 0; }
              50%   { inset: 35px 0 0 35px; }
              62.5% { inset: 0 0 0 35px; }
              75%   { inset: 0 0 35px 35px; }
              87.5% { inset: 0 0 35px 0; }
              100%  { inset: 0 35px 35px 0; }
            }
          </style>
          <div class="is-wrap">
            <div class="is-loader"></div>
          </div>
        `;
        this.sentinel.appendChild(spinner);
      }
      spinner.style.display = 'block';
    }

    _hideSpinner() {
      const spinner = this.sentinel.querySelector('.infinite-scroll-spinner');
      if (spinner) spinner.style.display = 'none';
    }

    _showFallbackButton() {
      this._hideSpinner();
      const btn = document.createElement('a');
      btn.href = this.nextUrl;
      btn.className = 'button button--outline';
      btn.style.marginTop = '1rem';
      btn.textContent = 'Load more products';
      this.sentinel.appendChild(btn);
    }
  }

  function initSentinel(sentinel) {
    if (sentinel._infiniteScrollInstance) return; // already watching this one
    new InfiniteScroll(sentinel);
  }

  function initAll() {
    document.querySelectorAll('.infinite-scroll-sentinel').forEach(initSentinel);
  }

  // ── Initial page load ───────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // ── Watch for new sentinels added to DOM after filter / sort updates ────
  //
  // WHY MutationObserver and NOT facet:update + setTimeout:
  //
  // When a filter is applied, theme.js listens to "facet:update" and does
  // an async fetch (takes 200-500ms). It only inserts the new sentinel into
  // the DOM AFTER that fetch completes (inside replaceChildren).
  //
  // If we listen to "facet:update" ourselves and do setTimeout(init, 0),
  // our setTimeout fires in ~1ms — long before the fetch finishes and the
  // new sentinel exists. So init() sees no new sentinel and does nothing.
  //
  // MutationObserver watches the actual DOM. It fires the moment
  // replaceChildren inserts the new sentinel, no matter how long the
  // fetch took. This is the only reliable way to catch it.
  //
  const domWatcher = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue; // skip text nodes

        // The sentinel itself was added
        if (node.classList && node.classList.contains('infinite-scroll-sentinel')) {
          initSentinel(node);
        }

        // The sentinel is inside a newly added subtree (replaceChildren adds
        // a whole section at once, so the sentinel is a descendant)
        node.querySelectorAll && node.querySelectorAll('.infinite-scroll-sentinel').forEach(initSentinel);
      }
    }
  });

  domWatcher.observe(document.body, {
    childList: true,  // watch for added/removed children
    subtree:   true   // watch the entire body subtree
  });

})();